import {
    AdviserRiskRow,
    BranchRiskRow,
    BrokerRiskRow,
    ClientRiskRow,
    DashboardSummaryRow,
    RiskRow,
    RiskSummary
} from "../risk-model/RiskSummary";
import {RiskDashboardCollapseState} from "./RiskDashboardCollapseState";
import {RiskRowEntityType} from "../RiskRowEntityType";
import {RiskDashboardChangeModel, RowChanges} from "./RiskDashboardChangeModel";
import {RiskLimits} from "../RiskSummaryRecord";
import {MarginLevel} from "../MarginLevel";
import {Optional} from "../../../../js/util/Optional";
import {flatMap} from "../../../../js/util/UtilFunctions";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";

export abstract class ViewModelRow implements RiskLimits {

    protected constructor(public readonly type: RiskRowEntityType,
                          public readonly entity: RiskRow,
                          public readonly collapsed: boolean,
                          protected readonly changes: RowChanges) {
    }

    public get id(): number {
        return this.entity.id;
    }

    public get name(): string {
        return this.entity.name;
    }

    public get currentStel(): number {
        return this.entity.currentStel;
    }

    public get stelLimit(): number | null {
        // "in" is used because we may be setting the change to "null" for removing a limit
        if ("stelLimit" in this.changes) {
            // Using "!" here because the type checker decides to add "undefined" as a possible value when using "in"
            return this.changes.stelLimit!;
        } else {
            return this.entity.stelLimit;
        }
    }

    public get overStelLimit(): boolean {
        return this.stelLimit != null && (this.currentStel > this.stelLimit);
    }

    public get currentClientMargin(): number {
        return this.entity.currentClientMargin;
    }

    public get currentAsxMargin() {
        return this.entity.currentAsxMargin;
    }

    public get marginLimit(): number | null {
        if ("marginLimit" in this.changes) {
            return this.changes.marginLimit!;
        } else {
            return this.entity.marginLimit;
        }
    }

    public get overMarginLimit(): boolean {
        return this.marginLimit != null && (this.currentClientMargin > this.marginLimit);
    }

    public get currentCbpl(): number {
        return this.entity.currentCbpl;
    }

    public get cbplLimit(): number | null {
        if ("cbplLimit" in this.changes) {
            return this.changes.cbplLimit!;
        } else {
            return this.entity.cbplLimit;
        }
    }

    public get overCbplLimit(): boolean {
        return this.cbplLimit != null && (this.currentCbpl > this.cbplLimit);
    }

    public get marginLevel(): MarginLevel {
        if ("marginLevel" in this.changes) {
            return this.changes.marginLevel!;
        } else {
            return this.entity.marginLevel;
        }
    }

    public hasUnsavedChanges(): boolean {
        return this.hasStelChanges() || this.hasCbplChanges() || this.hasMarginChanges() || this.hasLevelChanges();
    }

    private hasStelChanges(): boolean {
        return ("stelLimit" in this.changes) && (this.entity.stelLimit !== this.changes.stelLimit);
    }

    private hasCbplChanges(): boolean {
        return ("cbplLimit" in this.changes) && (this.entity.cbplLimit !== this.changes.cbplLimit);
    }

    private hasMarginChanges(): boolean {
        return ("marginLimit" in this.changes) && (this.entity.marginLimit !== this.changes.marginLimit);
    }

    private hasLevelChanges(): boolean {
        return ("marginLevel" in this.changes) && (this.entity.marginLevel !== this.changes.marginLevel);
    }

    public limits(): RiskLimits {
        const copy = ObjectUtil.copy(this.entity);
        return ObjectUtil.merge({}, copy, this.changes);
    }

    public abstract reset(): ViewModelRow;
}

export class BrokerViewRow extends ViewModelRow {

    private constructor(entity: BrokerRiskRow,
                        collapsed: boolean,
                        changes: RowChanges,
                        public readonly branches: ReadonlyArray<BranchViewRow>) {
        super(RiskRowEntityType.BROKER, entity, collapsed, changes);
    }

    public reset(): ViewModelRow {
        const entity = this.entity as BrokerRiskRow;
        return new BrokerViewRow(entity,
                                 this.collapsed,
                                 {},
                                 this.branches);
    }

    public static build(entity: BrokerRiskRow,
                        collapsed: boolean,
                        changes: RowChanges,
                        branches: ReadonlyArray<BranchViewRow>): BrokerViewRow {
        return new BrokerViewRow(entity, collapsed, changes, branches);
    }
}

export class BranchViewRow extends ViewModelRow {
    private constructor(entity: BranchRiskRow,
                        collapsed: boolean,
                        changes: RowChanges,
                        public readonly advisers: ReadonlyArray<AdviserViewRow>) {
        super(RiskRowEntityType.BRANCH, entity, collapsed, changes);
    }

    public reset(): ViewModelRow {
        const entity = this.entity as BranchRiskRow;
        return new BranchViewRow(entity, this.collapsed, {}, this.advisers);
    }

    public static build(entity: BranchRiskRow,
                        collapsed: boolean,
                        changes: RowChanges,
                        advisers: ReadonlyArray<AdviserViewRow>): BranchViewRow {
        return new BranchViewRow(entity, collapsed, changes, advisers);
    }
}

export class AdviserViewRow extends ViewModelRow {

    private constructor(entity: AdviserRiskRow,
                        collapsed: boolean,
                        changes: RowChanges,
                        public readonly clients: ReadonlyArray<ClientViewRow>) {
        super(RiskRowEntityType.ADVISER, entity, collapsed, changes);
    }

    public reset(): ViewModelRow {
        const entity = this.entity as AdviserRiskRow;
        return new AdviserViewRow(entity, this.collapsed, {}, this.clients);
    }

    public static build(entity: AdviserRiskRow,
                        collapsed: boolean,
                        changes: RowChanges,
                        clients: ReadonlyArray<ClientViewRow>): AdviserViewRow {
        return new AdviserViewRow(entity, collapsed, changes, clients);
    }
}

export class ClientViewRow extends ViewModelRow {
    private constructor(entity: ClientRiskRow,
                        collapsed: boolean,
                        changes: RowChanges) {
        super(RiskRowEntityType.CLIENT, entity, collapsed, changes);
    }

    public reset(): ViewModelRow {
        const entity = this.entity as ClientRiskRow;
        return new ClientViewRow(entity, this.collapsed, {});
    }

    public static build(entity: ClientRiskRow, collapsed: boolean, changes: RowChanges): ClientViewRow {
        return new ClientViewRow(entity, collapsed, changes);
    }
}

export class DashboardSummaryViewRow extends ViewModelRow {
    public reset(): ViewModelRow {
        throw Error("unsupported operation");
    }

    public static build(summary: DashboardSummaryRow): DashboardSummaryViewRow {
        return new DashboardSummaryViewRow(RiskRowEntityType.ORG, summary, false, {});
    }
}

export class RiskDashboardViewModel {

    private constructor(public readonly riskSummary: RiskSummary,
                        public readonly rootLevel: RiskRowEntityType,
                        public readonly rows: ReadonlyArray<ViewModelRow>) {
    }

    public get summary(): DashboardSummaryViewRow {
        return DashboardSummaryViewRow.build(this.riskSummary.summary);
    }

    public limits(type: RiskRowEntityType, id: number): Optional<RiskLimits> {
        if (type === RiskRowEntityType.ORG) {
            throw Error("Cannot save ORG type");
        } else if (type === RiskRowEntityType.BROKER) {
            const row = this.brokers.find((b) => b.entity.id === id);
            return Optional.of(row).map((r) => r.limits());
        } else if (type === RiskRowEntityType.BRANCH) {
            const row = this.branches.find((b) => b.entity.id === id);
            return Optional.of(row).map((r) => r.limits());
        } else if (type === RiskRowEntityType.ADVISER) {
            const row = this.advisers.find((a) => a.entity.id === id);
            return Optional.of(row).map((r) => r.limits());
        } else if (type === RiskRowEntityType.CLIENT) {
            const row = this.clients.find((c) => c.entity.id === id);
            return Optional.of(row).map((r) => r.limits());
        } else {
            throw Error(`Unknown Org Row Type: ${type}`);
        }
    }

    public get brokers(): ReadonlyArray<BrokerViewRow> {
        if (this.rootLevel === RiskRowEntityType.ORG) {
            return this.rows as ReadonlyArray<BrokerViewRow>;
        } else {
            return [];
        }
    }

    public get branches(): ReadonlyArray<BranchViewRow> {
        if (this.rootLevel === RiskRowEntityType.BROKER) {
            return this.rows as ReadonlyArray<BranchViewRow>;
        } else {
            return this.brokers.map((br) => br.branches).reduce(flatMap, []);
        }
    }

    public get advisers(): ReadonlyArray<AdviserViewRow> {
        if (this.rootLevel === RiskRowEntityType.BRANCH) {
            return this.rows as ReadonlyArray<AdviserViewRow>;
        } else {
            return this.branches.map((br) => br.advisers).reduce(flatMap, []);
        }
    }

    public get clients(): ReadonlyArray<ClientViewRow> {
        if (this.rootLevel === RiskRowEntityType.ADVISER) {
            return this.rows as ReadonlyArray<ClientViewRow>;
        } else {
            return this.advisers.map((adv) => adv.clients).reduce(flatMap, []);
        }
    }

    public static build(summary: RiskSummary,
                        collapseState: RiskDashboardCollapseState,
                        changes: RiskDashboardChangeModel): RiskDashboardViewModel {
        const rootLevel = summary.rootLevel;

        const clients = this.buildClients(summary.clients, collapseState, changes);
        const advisers = this.buildAdvisers(summary.advisers, collapseState, changes, clients);
        const branches = this.buildBranches(summary.branches, collapseState, changes, advisers);
        const brokers = this.buildBrokers(summary.brokers, collapseState, changes, branches);

        switch (rootLevel) {
            case RiskRowEntityType.ORG:
                return new RiskDashboardViewModel(summary, rootLevel, brokers);
            case RiskRowEntityType.BROKER:
                return new RiskDashboardViewModel(summary, rootLevel, branches);
            case RiskRowEntityType.BRANCH:
                return new RiskDashboardViewModel(summary, rootLevel, advisers);
            case RiskRowEntityType.ADVISER:
                return new RiskDashboardViewModel(summary, rootLevel, clients);
            case RiskRowEntityType.CLIENT:
                return new RiskDashboardViewModel(summary, rootLevel, []);
        }
    }

    private static buildClients(rows: ReadonlyArray<ClientRiskRow>,
                                collapseState: RiskDashboardCollapseState,
                                changeModel: RiskDashboardChangeModel): ReadonlyArray<ClientViewRow> {
        return rows.map((client) => {
            const collapsed = collapseState.get(RiskRowEntityType.CLIENT, client.id);
            const changes = changeModel.get(RiskRowEntityType.CLIENT, client.id);
            return ClientViewRow.build(client, collapsed, changes);
        });
    }

    private static buildAdvisers(rows: ReadonlyArray<AdviserRiskRow>,
                                 collapseState: RiskDashboardCollapseState,
                                 changeModel: RiskDashboardChangeModel,
                                 clients: ReadonlyArray<ClientViewRow>): ReadonlyArray<AdviserViewRow> {
        return rows.map((adviser) => {
            const collapsed = collapseState.get(RiskRowEntityType.ADVISER, adviser.id);
            const changes = changeModel.get(RiskRowEntityType.ADVISER, adviser.id);
            const clientIds = adviser.clients.map((cl) => cl.id);
            const adviserClients = clients.filter((cl) => clientIds.includes(cl.id));
            return AdviserViewRow.build(adviser, collapsed, changes, adviserClients);
        });
    }

    private static buildBranches(rows: ReadonlyArray<BranchRiskRow>,
                                 collapseState: RiskDashboardCollapseState,
                                 changeModel: RiskDashboardChangeModel,
                                 advisers: ReadonlyArray<AdviserViewRow>): ReadonlyArray<BranchViewRow> {
        return rows.map((branch) => {
            const collapsed = collapseState.get(RiskRowEntityType.BRANCH, branch.id);
            const changes = changeModel.get(RiskRowEntityType.BRANCH, branch.id);
            const adviserIds = branch.advisers.map((adv) => adv.id);
            const branchAdvisers = advisers.filter((adv) => adviserIds.includes(adv.id));
            return BranchViewRow.build(branch, collapsed, changes, branchAdvisers);
        });
    }

    private static buildBrokers(rows: ReadonlyArray<BrokerRiskRow>,
                                collapseState: RiskDashboardCollapseState,
                                changeModel: RiskDashboardChangeModel,
                                branches: ReadonlyArray<BranchViewRow>): ReadonlyArray<BrokerViewRow> {
        return rows.map((broker) => {
            const collapsed = collapseState.get(RiskRowEntityType.BROKER, broker.id);
            const changes = changeModel.get(RiskRowEntityType.BROKER, broker.id);
            const branchIds = broker.branches.map((br) => br.id);
            const brokerBranches = branches.filter((br) => branchIds.includes(br.id));
            return BrokerViewRow.build(broker, collapsed, changes, brokerBranches);
        });
    }
}
